/** Iconografía del lienzo de diseño: trazo de 2 px sobre una rejilla de 24. */
const base = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Trazo un poco más fino (1.8px) para íconos de acción pequeños en filas de tabla. */
const baseAccion = { ...base, width: 15, height: 15, strokeWidth: 1.8 };

export const IconoEditar = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

export const IconoEliminar = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

export const IconoComentario = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M21 11.5a8.5 8.5 0 1 1-3.8-7.1L21 3l-1.2 3.6a8.4 8.4 0 0 1 1.2 4.9Z" />
  </svg>
);

export const IconoMas = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/** Bifurcación (§8.5): marca el bloque de lógica condicional que cuelga de un campo disparador. */
export const IconoCondicional = (): JSX.Element => (
  <svg {...baseAccion}>
    <circle cx="6" cy="6" r="2.4" />
    <circle cx="6" cy="18" r="2.4" />
    <circle cx="18" cy="12" r="2.4" />
    <path d="M6 8.4V15.6M8.3 7l7.4 3.8M8.3 17l7.4-3.8" />
  </svg>
);

export const IconoEnviar = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4Z" />
  </svg>
);

export const IconoOjo = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const IconoOjoTachado = (): JSX.Element => (
  <svg {...baseAccion}>
    <path d="M2 12s3.6-7 10-7c1.7 0 3.2.4 4.5 1M22 12s-1.2 2.3-3.4 4.3M9.9 9.9a3 3 0 0 0 4.2 4.2M6.5 6.5C3.9 8.2 2 12 2 12s3.6 7 10 7c1.5 0 2.8-.3 4-.8" />
    <path d="M2 2l20 20" />
  </svg>
);

export const IconoCerrar = (): JSX.Element => (
  <svg {...base}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export const IconoLinterna = (): JSX.Element => (
  <svg {...base}>
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
  </svg>
);

export const IconoCheck = (): JSX.Element => (
  <svg {...baseAccion} strokeWidth={2.4}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const IconoAlerta = (): JSX.Element => (
  <svg {...base}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

export const IconoMenu = (): JSX.Element => (
  <svg {...base}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

/** Flecha para plegar la barra lateral (se gira 180° cuando está oculta). */
export const IconoPlegar = (): JSX.Element => (
  <svg {...base} width={16} height={16}>
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

/** Luna (para pasar a oscuro) y sol (para pasar a claro) — mismo trazo que el resto. */
export const IconoLuna = (): JSX.Element => (
  <svg {...base}>
    <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
  </svg>
);

export const IconoSol = (): JSX.Element => (
  <svg {...base}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

export const IconoCamara = (): JSX.Element => (
  <svg {...base}>
    <path d="M4 8h3l1.4-2.1A2 2 0 0 1 10.1 5h3.8a2 2 0 0 1 1.7.9L17 8h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </svg>
);

export const IconoArchivo = (): JSX.Element => (
  <svg {...base}>
    <path d="M21.5 12.5 13 21a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 9" />
  </svg>
);

export const IconoGaleria = (): JSX.Element => (
  <svg {...base}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="m4 17 5-5 4 4 3-3 4 4" />
  </svg>
);

export const IconoDashboard = (): JSX.Element => (
  <svg {...base}>
    <rect x="3" y="3" width="8" height="8" rx="1.5" />
    <rect x="13" y="3" width="8" height="5" rx="1.5" />
    <rect x="13" y="10" width="8" height="11" rx="1.5" />
    <rect x="3" y="13" width="8" height="8" rx="1.5" />
  </svg>
);

export const IconoOT = (): JSX.Element => (
  <svg {...base}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 4v5" />
  </svg>
);

export const IconoSolicitud = (): JSX.Element => (
  <svg {...base}>
    <path d="M4 4h16v12H8l-4 4z" />
  </svg>
);

export const IconoCampana = (): JSX.Element => (
  <svg {...base}>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

export const IconoActivo = (): JSX.Element => (
  <svg {...base}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <path d="M6.5 10v4M17.5 14v-4M6.5 14h11" />
  </svg>
);

export const IconoUbicacion = (): JSX.Element => (
  <svg {...base}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const IconoFalla = (): JSX.Element => (
  <svg {...base}>
    <path d="M12 9v4m0 4h.01M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
  </svg>
);

export const IconoUsuarios = (): JSX.Element => (
  <svg {...base}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0M16 6a3 3 0 0 1 0 6M22 20a5 5 0 0 0-4-5" />
  </svg>
);

export const IconoReporte = (): JSX.Element => (
  <svg {...base}>
    <path d="M3 3v18h18" />
    <rect x="7" y="10" width="3" height="7" />
    <rect x="13" y="6" width="3" height="11" />
  </svg>
);

export const IconoLicencia = (): JSX.Element => (
  <svg {...base}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20M6 15h4" />
  </svg>
);

export const IconoQr = (): JSX.Element => (
  <svg {...base}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <path d="M14 14h3v3h-3zM20 14v7M17 20h4" />
  </svg>
);

export const IconoActividad = (): JSX.Element => (
  <svg {...base}>
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

export const IconoProcedimiento = (): JSX.Element => (
  <svg {...base}>
    <path d="M14 2 20 8v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
    <path d="M9 13l2 2 4-4" />
  </svg>
);

export const IconoCatalogo = (): JSX.Element => (
  <svg {...base}>
    <path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2z" />
    <path d="M8 7h7M8 11h7" />
  </svg>
);

export const IconoParte = (): JSX.Element => (
  <svg {...base}>
    <path d="M12 2 3 7v10l9 5 9-5V7z" />
    <path d="M3 7l9 5 9-5M12 12v10" />
  </svg>
);

/* Estados de OT: mismo lenguaje visual (círculo + símbolo) para que se
   reconozcan de un vistazo en la etiqueta, el calendario y los botones de
   cambio de estado. */
export const IconoEstadoAbierta = (): JSX.Element => (
  <svg {...base}>
    <rect x="4" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a5 5 0 0 1 9-3" />
  </svg>
);

export const IconoEstadoEnEspera = (): JSX.Element => (
  <svg {...base}>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 9v6M14 9v6" />
  </svg>
);

export const IconoEstadoEnCurso = (): JSX.Element => (
  <svg {...base}>
    <path d="M20 12a8 8 0 1 1-3-6.2" />
    <path d="M20 3v5h-5" />
  </svg>
);

export const IconoEstadoCompleta = (): JSX.Element => (
  <svg {...base}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5 11 15l5-5.5" />
  </svg>
);

export const IconoEstadoVencida = (): JSX.Element => (
  <svg {...base}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

/* Prioridad como ícono (círculo relleno + flecha/guion) en vez de un punto de
   color, para que se lea igual de rápido pero sin depender solo del color. */
const baseRelleno = { ...base, fill: 'currentColor', stroke: 'none' };

export const IconoPrioridadBaja = (): JSX.Element => (
  <svg {...baseRelleno}>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 10.5 12 15l4-4.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

export const IconoPrioridadMedia = (): JSX.Element => (
  <svg {...baseRelleno}>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12h8" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" fill="none" />
  </svg>
);

export const IconoPrioridadAlta = (): JSX.Element => (
  <svg {...baseRelleno}>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 13.5 12 9l4 4.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

export const IconoCalendario = (): JSX.Element => (
  <svg {...base}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export const IconoLupa = (): JSX.Element => (
  <svg {...base}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

export const IconoMedidor = (): JSX.Element => (
  <svg {...base}>
    <path d="M12 21a9 9 0 1 1 9-9" />
    <path d="M12 12 16 8" />
    <circle cx="12" cy="12" r="1" />
  </svg>
);

/** Engranaje de configuración (tuerca): abre las opciones de un panel. */
export const IconoEngranaje = (): JSX.Element => (
  <svg {...baseAccion}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

/** Menú «más opciones»: tres puntos verticales, el mismo lenguaje que el
 * agarre de arrastre (puntos rellenos, no trazo) pero en una sola columna. */
export const IconoOpciones = (): JSX.Element => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

/** Agarre de arrastre (grip): rejilla de puntos rellenos, no un trazo — así se
 * lee como algo para tomar con el mouse, no como otro ícono de acción. */
export const IconoArrastrar = (): JSX.Element => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="5" r="1.8" />
    <circle cx="9" cy="12" r="1.8" />
    <circle cx="9" cy="19" r="1.8" />
    <circle cx="15" cy="5" r="1.8" />
    <circle cx="15" cy="12" r="1.8" />
    <circle cx="15" cy="19" r="1.8" />
  </svg>
);
